import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Calendar, Clock, Music, DollarSign, Filter, Search, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDownloadHistory, DownloadHistoryItem } from '@/hooks/useDownloadHistory';
import { useSequencePurchase } from '@/hooks/useSequencePurchase';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export const MyDownloadsContent = () => {
  const { downloads, loading, error } = useDownloadHistory();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'artist'>('date');
  const [filterBy, setFilterBy] = useState<'all' | 'free' | 'paid'>('all');

  // Filter and search downloads
  const filteredDownloads = downloads.filter(item => {
    const matchesSearch = !searchQuery || 
      item.sequence.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.sequence.artist_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterBy === 'all' || 
      (filterBy === 'free' && item.is_free) ||
      (filterBy === 'paid' && !item.is_free);

    return matchesSearch && matchesFilter;
  });

  // Sort downloads
  const sortedDownloads = [...filteredDownloads].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.sequence.title.localeCompare(b.sequence.title);
      case 'artist':
        return (a.sequence.artist_name || '').localeCompare(b.sequence.artist_name || '');
      case 'date':
      default:
        return new Date(b.download_date).getTime() - new Date(a.download_date).getTime();
    }
  });

  const handleAddToDisplayList = async (sequenceId: string, year: number) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_display_lists')
        .upsert({
          user_id: user.id,
          sequence_id: sequenceId,
          year: year
        }, {
          onConflict: 'user_id,sequence_id,year'
        });

      if (error) throw error;
      
      toast.success(`Added to ${year} display list`);
    } catch (err) {
      console.error('Error adding to display list:', err);
      toast.error('Failed to add to display list');
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-4">Sign In Required</h1>
          <p className="text-muted-foreground mb-8">Please sign in to view your download history.</p>
          <Button asChild>
            <Link to="/auth">Sign In</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">My Downloads</h1>
        <p className="text-muted-foreground">
          View and manage your downloaded light sequences. Re-download any sequence and add them to your display lists.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Downloads</CardTitle>
            <Download className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{downloads.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Free Sequences</CardTitle>
            <Music className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {downloads.filter(d => d.is_free).length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${downloads.reduce((sum, d) => sum + d.price_paid, 0).toFixed(2)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search sequences or artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date">Download Date</SelectItem>
            <SelectItem value="title">Title</SelectItem>
            <SelectItem value="artist">Artist</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterBy} onValueChange={(value: any) => setFilterBy(value)}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Filter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Downloads</SelectItem>
            <SelectItem value="free">Free Only</SelectItem>
            <SelectItem value="paid">Paid Only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Downloads List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center space-x-4">
                  <Skeleton className="h-16 w-16 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-[250px]" />
                    <Skeleton className="h-4 w-[200px]" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-destructive">Error loading downloads: {error}</p>
          </CardContent>
        </Card>
      ) : sortedDownloads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Download className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Downloads Yet</h3>
            <p className="text-muted-foreground mb-6">
              {searchQuery || filterBy !== 'all' 
                ? 'No downloads match your search criteria. Try adjusting your filters or search terms.'
                : 'Start browsing our collection of light sequences to build your library! Discover free and premium sequences for your displays.'
              }
            </p>
            <div className="space-y-3">
              <Button asChild size="lg" className="w-full max-w-xs">
                <Link to="/sequences">
                  <Music className="h-4 w-4 mr-2" />
                  Browse Sequences
                </Link>
              </Button>
              {(searchQuery || filterBy !== 'all') && (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setSearchQuery('');
                    setFilterBy('all');
                  }}
                  className="w-full max-w-xs"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {sortedDownloads.map((item) => (
            <DownloadHistoryCard 
              key={item.id} 
              item={item} 
              onAddToDisplayList={handleAddToDisplayList}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Individual download card component
const DownloadHistoryCard = ({ 
  item, 
  onAddToDisplayList 
}: { 
  item: DownloadHistoryItem;
  onAddToDisplayList: (sequenceId: string, year: number) => void;
}) => {
  const { handleDownload } = useSequencePurchase(item.sequence_id);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleReDownload = async () => {
    setIsDownloading(true);
    try {
      await handleDownload({
        id: item.sequence_id,
        title: item.sequence.title,
        file_url: item.sequence.file_url,
        price: item.sequence.price || 0,
        song: item.sequence.artist_name ? {
          artist: item.sequence.artist_name
        } : undefined,
        creator: { id: '' } // Placeholder
      } as any);
    } catch (error) {
      console.error('Re-download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-6">
        <div className="flex items-center space-x-4">
          {/* Sequence Image */}
          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center overflow-hidden">
            {item.sequence.image_url ? (
              <img 
                src={item.sequence.image_url} 
                alt={item.sequence.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <Music className="h-6 w-6 text-muted-foreground" />
            )}
          </div>

          {/* Sequence Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div className="min-w-0 flex-1">
                <Link 
                  to={`/sequence/${item.sequence_id}`}
                  className="font-semibold text-lg hover:text-primary transition-colors truncate block"
                >
                  {item.sequence.title}
                </Link>
                {item.sequence.artist_name && (
                  <p className="text-muted-foreground">by {item.sequence.artist_name}</p>
                )}
                <div className="flex items-center space-x-4 mt-2 text-sm text-muted-foreground">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-1" />
                    {new Date(item.download_date).toLocaleDateString()}
                  </div>
                  {item.sequence.duration && (
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      {Math.floor(item.sequence.duration / 60)}:{(item.sequence.duration % 60).toString().padStart(2, '0')}
                    </div>
                  )}
                  {item.sequence.software_type && (
                    <Badge variant="secondary">{item.sequence.software_type}</Badge>
                  )}
                </div>
              </div>

              {/* Price */}
              <div className="text-right ml-4">
                <div className="font-semibold">
                  {item.is_free ? 'Free' : `$${item.price_paid.toFixed(2)}`}
                </div>
                <Badge variant={item.is_free ? 'secondary' : 'default'} className="mt-1">
                  {item.is_free ? 'Free' : 'Purchased'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleReDownload}
              disabled={isDownloading}
              size="sm"
              variant="outline"
            >
              <Download className="h-4 w-4 mr-2" />
              {isDownloading ? 'Downloading...' : 'Re-download'}
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <Select onValueChange={(year) => onAddToDisplayList(item.sequence_id, parseInt(year))}>
              <SelectTrigger className="w-[160px]" size="sm">
                <Plus className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Add to Display" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2025">2025 Display</SelectItem>
                <SelectItem value="2024">2024 Display</SelectItem>
                <SelectItem value="2023">2023 Display</SelectItem>
                <SelectItem value="2022">2022 Display</SelectItem>
                <SelectItem value="2021">2021 Display</SelectItem>
                <SelectItem value="2020">2020 Display</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};